import { differenceInYears } from 'date-fns';
import { addIcons } from 'ionicons';
import { close } from 'ionicons/icons';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { LoadingService } from 'src/app/services/loading.service';
import { NotificationService } from 'src/app/services/notification/notification.service';

import { AsyncPipe, CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnInit, signal, input } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ApiResponse, ApiService } from '@movilidad/app-core';
import {
  IonButton,
  IonButtons,
  IonCheckbox,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonSegment,
  IonSegmentButton,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';
import { Campus, User } from '@shared/models';
import { Supplier } from '@shared/models/supplier.model';

import { injectModalDismiss } from '@shared/utils/overlay';

@Component({
  selector: 'app-register-saml-modal',
  imports: [
    IonHeader,
    IonToolbar,
    IonButtons,
    IonButton,
    IonIcon,
    IonContent,
    IonItem,
    IonLabel,
    IonCheckbox,
    IonSegment,
    IonSegmentButton,
    IonSelect,
    IonSelectOption,
    IonTitle,
    AsyncPipe,
    ReactiveFormsModule,
    IonFooter,
    CommonModule,
  ],
  templateUrl: './register-saml-modal.component.html',
  styleUrls: ['./register-saml-modal.component.css'],
})
export class RegisterSamlModalComponent implements OnInit {
  private apiService = inject(ApiService);
  private dismissModal = injectModalDismiss();
  private loadingService = inject(LoadingService);
  private notificationService = inject(NotificationService);
  private fb = inject(FormBuilder);

  readonly user = input<Partial<User> | null>(null);
  readonly mode = input<'register' | 'update' | 'read'>('register');
  /**
   * Single-use handoff minted by the SAML ACS. The API reads the identity to register
   * from this ticket alone — `user` is only here to prefill the form.
   */
  readonly ticket = input<string | null>(null);

  form: FormGroup = this.fb.group({
    campus: new FormControl<number | null>(null, { validators: Validators.required }),
    underAge: new FormControl<boolean>(false, { validators: Validators.requiredTrue }),
  });
  readonly showUnderAgeFormControl = signal(true);
  campus$: Observable<Campus[]> = this.getCampus();
  supplier$: Observable<Supplier | null> | undefined = this.form.get('campus')?.valueChanges.pipe(
    filter((campusId) => !!campusId),
    tap(() => this.loadingService.present()),
    switchMap((campusId) => this.getSupplier(campusId)),
    tap(() => this.loadingService.dismiss())
  );
  isLoading = signal(false);
  selectedTab = signal<'terms' | 'privacy' | 'student_regulations' | 'disciplinary_conduct'>('terms');

  constructor() {
    addIcons({ close });
  }

  ngOnInit(): void {
    setTimeout(() => {
      this.form.get('campus')?.setValue(this.user()?.campus_current || null, { emitEvent: true });
      const user = this.user();
      if (user?.birthday) {
        const birthday = new Date(user.birthday);
        const age = differenceInYears(new Date(), birthday);
        if (age >= 18) {
          this.form.get('underAge')?.setValue(true);
          this.showUnderAgeFormControl.set(false);
        }
      } else {
        this.form.get('underAge')?.setValue(true);
        this.showUnderAgeFormControl.set(false);
      }
    }, 0);
  }

  close() {
    this.dismissModal();
  }

  acceptTerms() {
    if (this.mode() === 'register') {
      this.registerSamlUser();
    } else {
      this.updateTermsAndPrivacy();
    }
  }

  private registerSamlUser() {
    this.isLoading.set(true);
    this.loadingService.present();
    this.apiService
      .post('saml2/register', {
        ticket: this.ticket(),
        campus_registered: this.form.get('campus')?.value,
        terms: true,
      })
      .pipe(
        take(1),
        catchError((err: unknown) => {
          // 4xx bodies carry user-facing Spanish copy (e.g. the account already exists
          // because a duplicate submit won the race); 5xx stay generic, so keep the
          // retry prompt for those.
          const msg =
            err instanceof HttpErrorResponse && err.status < 500 ? err.error?.error || err.error?.message : null;
          this.notificationService.presentToast(
            msg || 'Error al registrar usuario SAML. Favor de intentar nuevamente.'
          );
          return of(null);
        })
      )
      .subscribe((response) => {
        if (response?.api_token && response?.user) {
          this.dismissModal(response);
        }
        this.isLoading.set(false);
        this.loadingService.dismiss();
      });
  }

  private updateTermsAndPrivacy() {
    this.isLoading.set(true);
    this.loadingService.present();
    this.apiService
      .put('profile/terms_privacy', {
        terms: 1,
        privacy: 1,
      })
      .pipe(
        take(1),
        catchError(() => {
          this.notificationService.presentToast('Favor de intentar nuevamente.');
          return of(null);
        })
      )
      .subscribe((response) => {
        console.log({ response });
        if (response) {
          this.dismissModal(response);
        }
        this.isLoading.set(false);
        this.loadingService.dismiss();
      });
  }

  private getCampus() {
    return this.apiService.get<ApiResponse<Campus[]>>('campus').pipe(
      map((res) => res.data),
      catchError(() => of([]))
    );
  }

  private getSupplier(campusId: number): Observable<Supplier | null> {
    return this.apiService.get<Supplier>(`supplier?campus_id=${campusId}`).pipe(catchError(() => of(null)));
  }
}
